import { html, LitElement, PropertyValues } from "lit";
import { customElement, state } from "lit/decorators.js";
import { OrderManagementContext, orderManagementContextKey } from "../order-management.context";
import { consume } from "@lit/context";
import { Subscription } from "rxjs";
import { AddOrUpdateCustomerInformationDto, OrderDtoReadable } from "../../api";
import { produce } from "immer";
import z from "zod";
import { FormState, ValidationContext } from "../../core/validation.context";
import { RequiredNonEmptyString } from "../../core/helpers";
import { ifDefined } from "lit/directives/if-defined.js";
import { PaymentButtonElement } from "./payment-button.element";
import { createRef, ref } from "lit/directives/ref.js";
import { ModalRequestEventDetail } from "../../core/components/content-modal.element";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { when } from "lit/directives/when.js";
import { repeat } from "lit/directives/repeat.js";
import { choose } from "lit/directives/choose.js";


type CustomerDetailsObject = AddOrUpdateCustomerInformationDto & {
    termsAndConditions: boolean;
};

@customElement("customer-details-form")
export class CustomerDetailsFormElement extends LitElement {
    private customerSchema = z.object({
        title: z.string().optional(),
        firstName: RequiredNonEmptyString("First name is required"),
        lastName: RequiredNonEmptyString("Last name is required"),
        phone: RequiredNonEmptyString("Phone number is required"),
        email: z.email("Email must be a valid email address"),
        address1: RequiredNonEmptyString("Address line 1 is required"),
        address2: z.string().optional(),
        townOrCity: z.string().optional(),
        county: z.string().optional(),
        postalCode: RequiredNonEmptyString("Postal code is required"),
        termsAndConditions: z.boolean().refine((val) => val === true, "You must accept the terms and conditions"),
    });

    private _customerDetailsObject: CustomerDetailsObject = {
        termsAndConditions: false,
        firstName: '',
        lastName: '',
        phone: '',
        email: ''
    };
    private _paymentButtonElement = createRef<PaymentButtonElement>();

    private _formState? : FormState | null = null;
    private _customerDetailsValidationContext = new ValidationContext(this.customerSchema, this._customerDetailsObject);


    private _orderStateSubscription: Subscription | undefined;

    @state()
    private _orderPropertyValuesProvided: boolean = false;

    @state()
    private _paymentButtonDisabled: boolean = true;

    @state()
    private _paymentButtonLoading: boolean = false;

    @state()
    private _orderState : OrderDtoReadable | null = null;

    @consume({ context: orderManagementContextKey, subscribe: true })
    @state()
    private _orderManagementContext: OrderManagementContext | null = null;

    constructor() {
        super();
        this._customerDetailsValidationContext.formState$.subscribe((formState) => {
            this._formState = formState;
            this._paymentButtonDisabled = !this._customerDetailsValidationContext.validate(true).outcome;
            this.requestUpdate();
        });
    }

    protected override updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);
        if(_changedProperties.has("_orderManagementContext")) {

            if(this._orderStateSubscription) {
                this._orderStateSubscription.unsubscribe();
            }

            this._orderStateSubscription = this._orderManagementContext?.orderState$.subscribe((next) => {
                if(next?.customer) {
                    this._customerDetailsObject.address1 = next.customer.address1 ?? '';
                    this._customerDetailsObject.address2 = next.customer.address2 ?? '';
                    this._customerDetailsObject.county = next.customer.county ?? '';
                    this._customerDetailsObject.email = next.customer.email ?? '';
                    this._customerDetailsObject.firstName = next.customer.firstName ?? '';
                    this._customerDetailsObject.lastName = next.customer.lastName ?? '';
                    this._customerDetailsObject.phone = next.customer.phone ?? '';
                    this._customerDetailsObject.postalCode = next.customer.postalCode ?? '';
                    this._customerDetailsObject.title = next.customer.title ?? '';
                    this._customerDetailsObject.townOrCity = next.customer.townOrCity ?? '';
                    this._customerDetailsObject.marketingConsent = next.customer.marketingConsent ?? false;
                    this._customerDetailsObject.termsAndConditions = false;
                    this._orderPropertyValuesProvided = true;
                    this._customerDetailsValidationContext.validate();
                }
                this._orderState = next;

                this.updateOrderPropertyValuesProvided();

            });
        }
    }

    protected override firstUpdated() {
        this._customerDetailsValidationContext.setupListeners(this);
        this._paymentButtonElement.value?.addEventListener("payment-button-click", (event) => this.handlePaymentButtonClick(event));
    }


    private async handleTextFieldInput(event: Event & { target: HTMLInputElement }, fieldName: CustomerDetailsTextFormData) {
        switch (fieldName) {
            case "title":
                this._customerDetailsObject.title = event.target.value;
                break;
            case "firstName":
                this._customerDetailsObject.firstName = event.target.value;
                break;
            case "lastName":
                this._customerDetailsObject.lastName = event.target.value;
                break;
            case "phone":
                this._customerDetailsObject.phone = event.target.value;
                break;
            case "email":
                this._customerDetailsObject.email = event.target.value;
                break;
            case "address1":
                this._customerDetailsObject.address1 = event.target.value;
                break;
            case "address2":
                this._customerDetailsObject.address2 = event.target.value;
                break;
            case "townOrCity":
                this._customerDetailsObject.townOrCity = event.target.value;
                break;
            case "county":
                this._customerDetailsObject.county = event.target.value;
                break;
            case "postalCode":
                this._customerDetailsObject.postalCode = event.target.value;
                break;
        }
    }

    private async handleAdditionalInfoField(event: Event & { target: HTMLInputElement }, fieldKey: string | null) {
        if(fieldKey === null || this._customerDetailsObject === null) {
            return;
        }

        if(this._customerDetailsObject.orderPropertyValues === undefined) {
            this._customerDetailsObject.orderPropertyValues = [];
        }

        let value = this._customerDetailsObject.orderPropertyValues?.find(orderProperty => orderProperty.key === fieldKey) ?? null;
        if(value === null) {
            value = {
                key: fieldKey,
            }
            this._customerDetailsObject.orderPropertyValues?.push(value);
        }

        value.value = event.target.value;
        this.updateOrderPropertyValuesProvided();
    }

    private async handleCheckboxChange(event: Event & { target: HTMLInputElement }, fieldName: CustomerDetailsCheckboxFormData) {

        if(fieldName === "termsAndConditions") {
            this._customerDetailsObject.termsAndConditions = event.target.checked;
            this._paymentButtonDisabled = !this._customerDetailsValidationContext.validate(true).outcome;
            return;
        }

        const previousValue = this._customerDetailsObject;

        const nextValue = produce(previousValue ?? {}, draft => {
            switch (fieldName) {
                case "marketingConsent":
                    draft.marketingConsent = event.target.checked;
                    break;
            }
        });

        await this._orderManagementContext?.addCustomerDetails(nextValue);

    }

    private async handlePaymentButtonClick(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        if(this._paymentButtonLoading || this._paymentButtonDisabled) {
            return;
        }
        this._paymentButtonLoading = true;
        if(await this._orderManagementContext?.addCustomerDetails(this._customerDetailsObject))
        {
            await this._orderManagementContext?.getPaymentRedirect();
        }
        this._paymentButtonLoading = false;
    }

    private handleTermsClick() {

        const htmlContent = document.getElementById("termsAndConditionsContent")?.innerHTML;

        if(!htmlContent || htmlContent.length === 0) {
            return;
        }

        this.dispatchEvent(new CustomEvent<ModalRequestEventDetail>("show-modal-requested", {
            bubbles: true,
            composed: true,
            detail: {
                title: "Terms and Conditions",
                content: html`${unsafeHTML(htmlContent)}`
            }
        }));
    }

    private updateOrderPropertyValuesProvided() {
        this._orderPropertyValuesProvided = this._orderState?.orderProperties?.reduce<boolean>((valuesProvided, orderPropertyDefinition) => {
                if(!valuesProvided) {
                    return false;
                }
                const existingValue = this._orderState?.orderProperties?.find(orderProperty => orderProperty.key === orderPropertyDefinition.key)?.existingValue;
                const setValue = this._customerDetailsObject?.orderPropertyValues?.find(orderProperty => orderProperty.key === orderPropertyDefinition.key)?.value;
                return valuesProvided && (!!existingValue || !!setValue);
            }, true) ?? true;
    }


    protected override render() {
        const defaultFieldClass = "mt-4 group";
        const defaultLabelClass = "block after:ml-1 group-has-[input:required]:after:content-['*'] group-has-[input:required]:after:text-red-500 text-sm/6 font-medium text-gray-700";
        const defaultInputClass = "peer block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-mcr-purple-600 sm:text-sm/6 aria-invalid:outline-red-500";


        return html`
            <div class="bg-white rounded-lg p-4">
                <div class="mb-4 text-2xl text-mcr-purple">Customer Information</div>
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-12">
                    <div class="${defaultFieldClass} lg:col-span-4">
                        <label for="title" class="${defaultLabelClass}">Title</label>
                        <input id="title"
                            type="text"
                            autocomplete="honorific-prefix"
                            class="${defaultInputClass}"
                            data-validate-field="title"
                            .value="${this._orderState?.customer?.title ?? ''}"
                            @input="${(event: Event) => this.handleTextFieldInput(event as Event & { target: HTMLInputElement }, 'title')}"
                            aria-invalid=${this._customerDetailsValidationContext.isInvalid('title')}
                            aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('title'))} />
                        ${this.renderValidationForField('title')}
                    </div>
                    <div class="${defaultFieldClass} lg:col-span-4">
                        <label for="firstName" class="${defaultLabelClass}">First name</label>
                        <input id="firstName"
                            required
                            type="text"
                            autocomplete="given-name"
                            class="${defaultInputClass}"
                            data-validate-field="firstName"
                            .value="${this._orderState?.customer?.firstName ?? ''}"
                            @input="${(event: Event) => this.handleTextFieldInput(event as Event & { target: HTMLInputElement }, 'firstName')}"
                            aria-invalid=${this._customerDetailsValidationContext.isInvalid('firstName')}
                            aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('firstName'))} />
                        ${this.renderValidationForField('firstName')}
                    </div>
                    <div class="${defaultFieldClass} lg:col-span-4">
                        <label for="lastName" class="${defaultLabelClass}">Last name</label>
                        <input id="lastName"
                            required
                            type="text"
                            autocomplete="family-name"
                            class="${defaultInputClass}"
                            data-validate-field="lastName"
                            .value="${this._orderState?.customer?.lastName ?? ''}"
                            @input="${(event: Event) => this.handleTextFieldInput(event as Event & { target: HTMLInputElement }, 'lastName')}"
                            aria-invalid=${this._customerDetailsValidationContext.isInvalid('lastName')}
                            aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('lastName'))} />
                        ${this.renderValidationForField('lastName')}
                    </div>


                    <div class="${defaultFieldClass} lg:col-span-6">
                        <label for="address1" class="${defaultLabelClass}">Address 1</label>
                        <input id="address1"
                            required
                            type="text"
                            autocomplete="billing address-line1"
                            class="${defaultInputClass}"
                            data-validate-field="address1"
                            .value="${this._orderState?.customer?.address1 ?? ''}"
                            @input="${(event: Event) => this.handleTextFieldInput(event as Event & { target: HTMLInputElement }, 'address1')}"
                            aria-invalid=${this._customerDetailsValidationContext.isInvalid('address1')}
                            aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('address1'))} />
                        ${this.renderValidationForField('address1')}
                    </div>
                    <div class="${defaultFieldClass} lg:col-span-6">
                        <label for="address2" class="${defaultLabelClass}">Address 2</label>
                        <input id="address2"
                            type="text"
                            autocomplete="billing address-line2"
                            class="${defaultInputClass}"
                            data-validate-field="address2"
                            .value="${this._orderState?.customer?.address2 ?? ''}"
                            @input="${(event: Event) => this.handleTextFieldInput(event as Event & { target: HTMLInputElement }, 'address2')}"
                            aria-invalid=${this._customerDetailsValidationContext.isInvalid('address2')}
                            aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('address2'))} />
                        ${this.renderValidationForField('address2')}
                    </div>

                    <div class="${defaultFieldClass} lg:col-span-4">
                        <label for="town" class="${defaultLabelClass}">Town</label>
                        <input id="town"
                            type="text"
                            autocomplete="billing address-level2"
                            class="${defaultInputClass}"
                            data-validate-field="townOrCity"
                            .value="${this._orderState?.customer?.townOrCity ?? ''}"
                            @input="${(event: Event) => this.handleTextFieldInput(event as Event & { target: HTMLInputElement }, 'townOrCity')}"
                            aria-invalid=${this._customerDetailsValidationContext.isInvalid('townOrCity')}
                            aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('townOrCity'))} />
                        ${this.renderValidationForField('townOrCity')}
                    </div>
                    <div class="${defaultFieldClass} lg:col-span-4">
                        <label for="county" class="${defaultLabelClass}">County</label>
                        <input id="county"
                            type="text"
                            autocomplete="billing address-level1"
                            class="${defaultInputClass}"
                            data-validate-field="county"
                            .value="${this._orderState?.customer?.county ?? ''}"
                            @input="${(event: Event) => this.handleTextFieldInput(event as Event & { target: HTMLInputElement }, 'county')}"
                            aria-invalid=${this._customerDetailsValidationContext.isInvalid('county')}
                            aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('county'))} />
                        ${this.renderValidationForField('county')}
                    </div>
                    <div class="${defaultFieldClass} lg:col-span-4">
                        <label for="postcode" class="${defaultLabelClass}">Postcode</label>
                        <input id="postcode"
                            required
                            type="text"
                            autocomplete="billing postal-code"
                            class="${defaultInputClass}"
                            data-validate-field="postalCode"
                            .value="${this._orderState?.customer?.postalCode ?? ''}"
                            @input="${(event: Event) => this.handleTextFieldInput(event as Event & { target: HTMLInputElement }, 'postalCode')}"
                            aria-invalid=${this._customerDetailsValidationContext.isInvalid('postalCode')}
                            aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('postalCode'))} />
                        ${this.renderValidationForField('postalCode')}
                    </div>


                    <div class="${defaultFieldClass} lg:col-span-6">
                        <label for="phone" class="${defaultLabelClass}">Phone</label>
                        <input id="phone"
                            required
                            type="tel"
                            autocomplete="billing tel"
                            class="${defaultInputClass}"
                            data-validate-field="phone"
                            .value="${this._orderState?.customer?.phone ?? ''}"
                            @input="${(event: Event) => this.handleTextFieldInput(event as Event & { target: HTMLInputElement }, 'phone')}"
                            aria-invalid=${this._customerDetailsValidationContext.isInvalid('phone')}
                            aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('phone'))} />
                        ${this.renderValidationForField('phone')}
                    </div>
                    <div class="${defaultFieldClass} lg:col-span-6">
                        <label for="email" class="${defaultLabelClass}">Email</label>
                        <input id="email"
                            required
                            type="email"
                            autocomplete="billing email"
                            class="${defaultInputClass}"
                            data-validate-field="email"
                            .value="${this._orderState?.customer?.email ?? ''}"
                            @input="${(event: Event) => this.handleTextFieldInput(event as Event & { target: HTMLInputElement }, 'email')}"
                            aria-invalid=${this._customerDetailsValidationContext.isInvalid('email')}
                            aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('email'))} />
                        ${this.renderValidationForField('email')}
                    </div>


                        <div class="mt-8 lg:col-span-12">
                            ${when(this._orderState?.orderProperties && this._orderState?.orderProperties.length > 0, () => html`
                                <div>Additional Information</div>
                                <div class="flex flex-col gap-4"></div>`)
                            }
                            ${repeat(this._orderState?.orderProperties ?? [], orderProperty => orderProperty.key, (item, index) => {
                                const inputType = (() => {
                                    switch(item.dataType) {
                                        case 1:
                                            return "text";
                                        case 2:
                                            return "textarea";
                                        case 3:
                                            return "number";
                                        case 4:
                                            return "date";
                                        case 5:
                                            return "checkbox";
                                        default:
                                            return "text";
                                    }
                                })();

                                return html`
                                    ${choose(inputType, [
                                        ['text', () => html`
                                            <div class="${defaultFieldClass} flex flex-col">
                                                <label class="${defaultLabelClass}">${item.title}</label>
                                                <input type="text"
                                                        class="${defaultInputClass}"
                                                        .value="${item.existingValue ?? ''}"
                                                        ?required="${item.isRequired}"
                                                        @change="${(event: Event) => this.handleAdditionalInfoField(event as Event & { target: HTMLInputElement }, item.key)}" />
                                            </div>
                                        `],
                                        ['number', () => html`
                                            <div class="${defaultFieldClass} flex flex-col">
                                                <label class="${defaultLabelClass}">${item.title}</label>
                                                <input type="number"
                                                class="${defaultInputClass}"
                                                .value="${item.existingValue ?? ''}"
                                                ?required="${item.isRequired}"
                                                @change="${(event: Event) => this.handleAdditionalInfoField(event as Event & { target: HTMLInputElement }, item.key)}" />
                                            </div>
                                        `],
                                        ['date', () => html`
                                            <div class="${defaultFieldClass} flex flex-col">
                                                <label class="${defaultLabelClass}">${item.title}</label>
                                                <input type="date"
                                                class="${defaultInputClass}"
                                                .value="${item.existingValue ?? ''}"
                                                ?required="${item.isRequired}"
                                                @change="${(event: Event) => this.handleAdditionalInfoField(event as Event & { target: HTMLInputElement }, item.key)}" />
                                            </div>
                                        `],
                                        ['checkbox', () => html`
                                            <div class="${defaultFieldClass} flex gap-3">
                                                <div class="flex h-6 shrink-0 items-center">
                                                    <div class="group grid size-4 grid-cols-1">
                                                        <input type="checkbox"
                                                            class="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-mcr-purple-600 checked:bg-mcr-purple-600 indeterminate:border-mcr-purple-600 indeterminate:bg-mcr-purple-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mcr-purple-600 disabled:border-gray-300 disabled:bg-gray-100 forced-colors:appearance-auto disabled:checked:bg-gray-100"
                                                            ?checked="${item.existingValue === "true" ? true : false}"
                                                            ?required="${item.isRequired}"
                                                            @change="${(event: Event) => this.handleAdditionalInfoField(event as Event & { target: HTMLInputElement }, item.key)}" />
                                                        <svg viewBox="0 0 14 14" fill="none" class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25">
                                                            <path d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-has-[:checked]:opacity-100" />
                                                            <path d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-has-[:indeterminate]:opacity-100" />
                                                        </svg>
                                                    </div>
                                                </div>
                                                <label class="text-sm/6 font-medium text-gray-900">${item.title}</label>
                                            </div>

                                        `],
                                        ['textarea', () => html`
                                            <div class="${defaultFieldClass} flex flex-col">
                                                <label class="${defaultLabelClass}">${item.title}</label>
                                                <textarea
                                                    class="${defaultInputClass}"
                                                    @change="${(event: Event) => this.handleAdditionalInfoField(event as Event & { target: HTMLInputElement }, item.key)}"
                                                    ?required="${item.isRequired}"
                                                >${item.existingValue ?? ''}</textarea>
                                            </div>
                                        `],

                                    ],() => html``)}
                                    ${when(item.description && item.description.length > 0, () => html`
                                        <small class="text-gray-500">${item.description}</small>
                                    `)}
                            `})}
                        </div>
                    </div>

                    <div class="${defaultFieldClass} mt-6 lg:col-span-12">
                        <div class="flex gap-3">
                            <div class="flex h-6 shrink-0 items-center">
                                <div class="group grid size-4 grid-cols-1">
                                    <input id="termsAndConditions"
                                        type="checkbox"
                                        class="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-mcr-purple-600 checked:bg-mcr-purple-600 indeterminate:border-mcr-purple-600 indeterminate:bg-mcr-purple-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mcr-purple-600 disabled:border-gray-300 disabled:bg-gray-100 forced-colors:appearance-auto disabled:checked:bg-gray-100"
                                        @input="${(event: Event) => this.handleCheckboxChange(event as Event & { target: HTMLInputElement }, 'termsAndConditions')}"
                                        data-validate-field="termsAndConditions"
                                        .checked="${this._customerDetailsObject.termsAndConditions}"
                                        aria-invalid=${this._customerDetailsValidationContext.isInvalid('termsAndConditions')}
                                        aria-errormessage=${ifDefined(this._customerDetailsValidationContext.firstErrorFor('termsAndConditions'))} />
                                    <svg viewBox="0 0 14 14" fill="none" class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25">
                                        <path d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-has-[:checked]:opacity-100" />
                                        <path d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-has-[:indeterminate]:opacity-100" />
                                    </svg>
                                </div>
                            </div>
                            <label for="termsAndConditions" class="text-sm/6 font-medium text-gray-900">I agree to the <a class="text-mcr-purple hover:underline" href="#" @click="${() => this.handleTermsClick()}">terms and conditions</a></label>
                        </div>
                        ${this.renderValidationForField('termsAndConditions')}
                    </div>

                    <div class="${defaultFieldClass} mt-6 flex gap-3 lg:col-span-12">
                        <div class="flex h-6 shrink-0 items-center">
                            <div class="group grid size-4 grid-cols-1">
                                <input id="marketingConsent"
                                    type="checkbox"
                                    class="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-mcr-purple-600 checked:bg-mcr-purple-600 indeterminate:border-mcr-purple-600 indeterminate:bg-mcr-purple-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mcr-purple-600 disabled:border-gray-300 disabled:bg-gray-100 forced-colors:appearance-auto disabled:checked:bg-gray-100"
                                    .checked="${this._orderState?.customer?.marketingConsent ?? false}"
                                    @change="${(event: Event) => this.handleCheckboxChange(event as Event & { target: HTMLInputElement }, 'marketingConsent')}" />
                                <svg viewBox="0 0 14 14" fill="none" class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25">
                                    <path d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-has-[:checked]:opacity-100" />
                                    <path d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-has-[:indeterminate]:opacity-100" />
                                </svg>
                            </div>
                        </div>
                        <label for="marketingConsent" class="text-sm/6 font-medium text-gray-900">I agree to receive marketing communications</label>
                    </div>
                    <div class="flex justify-end">
                        <payment-button
                            ${ref(this._paymentButtonElement)}
                            class="inline-block mt-6"
                            buttonText="Proceed to Payment"
                            ?disabled="${this._paymentButtonDisabled || !this._orderPropertyValuesProvided}"
                            ?loading="${this._paymentButtonLoading}"></payment-button>
                    </div>
                </div>
            </div>
        `;
    }

    private renderValidationForField(fieldName: CustomerDetailsTextFormData | CustomerDetailsCheckboxFormData) {
        if(this._customerDetailsValidationContext.isInvalid(fieldName)) {
            return html` <small class="text-red-500">${this._customerDetailsValidationContext.firstErrorFor(fieldName)}</small>`;
        }
    }

    protected override createRenderRoot() {
        return this;
    }
}

type CustomerDetailsTextFormData = "title" | "firstName" | "lastName" | "phone" | "email" | "address1" | "address2" | "townOrCity" | "county" | "postalCode";
type CustomerDetailsCheckboxFormData = "termsAndConditions" | "marketingConsent";